import { BUTTON_SIZE, BUTTON_TYPE, DSButton, DSModal } from '@repo/design-system/index'
import { useEffect, useRef, useState } from 'react'
import { n_ai_tutor, n_common } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { ConversationErrorPopup } from '../../../new-portal/conversational-ai/ConversationPopup'
import LoadingSpinner from '../../LoadingSpinner'
import StartRecord from './StartRecord'
import WhisperRecognition from './WhisperRecognition'
import { WebAudioRecorder } from './WhisperRecorder'

// NEW VERSION OF RECORDING
const NoRecognizedModal = ({ show, setShow }) => {
  const { t } = useTranslation()
  return (
    <DSModal id="aiTutorQuitConfirm" isOpen={show} setIsOpen={setShow} size={'sm'} closeButton={false}>
      <DSModal.Content className="mt-[16px]">{t(n_ai_tutor.common.popup_body1)}</DSModal.Content>
      <DSModal.ButtonContainer direction="horizontal">
        <DSButton
          size={BUTTON_SIZE.MD}
          type={BUTTON_TYPE.SOLID}
          onClick={() => {
            setShow(false)
          }}
        >
          <DSButton.Label>{t(n_common.btn_ok)}</DSButton.Label>
        </DSButton>
      </DSModal.ButtonContainer>
    </DSModal>
  )
}

const WhisperRecord = ({
  handleStopRecording,
  setIsRecording: setIsRecording,
  isLoading: externalLoading,
  beforeRecord: beforeRecord,
  noMargin = false,
  helper = null,
  className = '',
  errorModal = true,
  showNudge = false,
  handleSessionSummary = null,
  waitingTranscription = false,
  setWaitingTranscription = null,
}) => {
  const recorderRef = useRef<WebAudioRecorder | null>(null)
  const [recording, setRecording] = useState(false)
  const [waveform, setWaveform] = useState<number[]>([])
  const [isResourceAllocated, setIsResourceAllocated] = useState(false)
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [showNoRecognizedModal, setShowNoRecognizedModal] = useState(false)
  const [isSpeaking, setIsSpeaking] = useState(false)

  const onWaveformData = (wf: number[]) => {
    setWaveform(wf)
  }

  const handleStart = async () => {
    beforeRecord?.()
    setIsRecording(true)
    if (recorderRef.current) return // 이미 객체가 존재하면 다시 생성 방지
    setRecording(true)

    const recorder = new WebAudioRecorder({
      sampleRate: 44100,
      maxRecordTimeMs: 5 * 60 * 1000, // 5분
      onWaveFormData: onWaveformData,
      isSpeaking: setIsSpeaking,
      onStart: () => {
        console.log('녹음 시작')
        setIsResourceAllocated(true)
        setRecording(true)
      },
      onStop: () => {
        console.log('녹음 종료')
        setRecording(false)
        setIsResourceAllocated(false)
        recorderRef.current = null
      },
      onCancel: () => {
        console.log('녹음 취소됨')
        setRecording(false)
        setIsResourceAllocated(false)
        recorderRef.current = null
      },
      onError: (err) => {
        console.error('녹음 에러:', err)
        setRecording(false)
        setIsResourceAllocated(false)
        recorderRef.current = null
      },
    })
    recorderRef.current = recorder
    await recorder.startRecording()
  }

  // 녹음 완료(사용자 수동 종료)
  const handleStop = async () => {
    setIsRecording(false)
    if (!recorderRef.current) return
    setWaitingTranscription(true)

    const wavData = await recorderRef.current.stopRecording()
    if (wavData) {
      try {
        if (wavData.byteLength === 0) {
          setShowNoRecognizedModal(true)
          setWaitingTranscription(false)
          return
        }

        const audioBlob = new Blob([wavData], { type: 'audio/wav' })
        const file = new File([audioBlob], 'audio.wav', { type: 'audio/wav' })
        const formData = new FormData()
        formData.append('file', file, file.name)

        const resp = await fetch('/api/v1/transcribe', {
          method: 'POST',
          body: formData,
        })

        if (!resp.ok) {
          throw new Error('Transcription request failed.')
        }

        const data = await resp.json()
        console.log('Transcribed text:', data.text)

        if (!data.text) {
          setShowNoRecognizedModal(true)
          setWaitingTranscription(false)
          return
        }

        handleStopRecording(data.text, wavData)
      } catch (err) {
        console.error('Error uploading or transcribing:', err)
        setShowErrorModal(true)
        setWaitingTranscription(false)
      }
    }
    setWaitingTranscription(false)
  }

  const handleCancel = async () => {
    setIsRecording(false)
    if (!recorderRef.current) return
    await recorderRef.current.cancelRecording()
  }

  useEffect(() => {
    return () => {
      console.log('UNMOUNTED')
      handleCancel()
    }
  }, [])

  return (
    <div
      className={`w-full bg-white opacity-95 flex flex-col bottom-[0px] items-center justify-center ${
        noMargin ? (recording ? '' : 'py-[12px]') : 'pt-[12px] px-[34px] pb-[52px]'
      } mt-[auto] ${className}`}
    >
      {waitingTranscription ? (
        <LoadingSpinner lg show primary />
      ) : !recording ? (
        isResourceAllocated ? (
          <LoadingSpinner lg show primary />
        ) : (
          <StartRecord
            isSessionClosing={false}
            onClickStart={handleStart}
            loading={externalLoading || waitingTranscription || recording}
            handleSessionSummary={handleSessionSummary}
            showNudge={showNudge}
            helper={helper}
          />
        )
      ) : !isResourceAllocated ? (
        <LoadingSpinner lg show primary />
      ) : (
        <WhisperRecognition
          handleCancel={handleCancel}
          handleStop={handleStop}
          waitingTranscription={waitingTranscription}
          waveformData={waveform}
          isSpeaking={isSpeaking}
        />
      )}
      {errorModal && <ConversationErrorPopup showErrorModal={showErrorModal} setShowErrorModal={setShowErrorModal} />}
      <NoRecognizedModal show={showNoRecognizedModal} setShow={setShowNoRecognizedModal} />
    </div>
  )
}

export default WhisperRecord
